/**
 * Per-answer disclosure of `judgment` stream events: spans of the answer the
 * model asserted without a source behind them
 * (`AssistantAnswerProvenance === 'judgment'`).
 *
 * Quiet, collapsed, and last — exactly like ThreadUnplacedSources, and for
 * the same reason: an assistant turn's judgment calls belong to THAT turn's
 * answer, not to a thread-wide rail, so this renders inline per turn rather
 * than in ThreadReferencesSidebar.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

interface ThreadJudgmentNoteProps {
  /** `judgment` event notes for this turn, in arrival order. Omitted or
   * empty (including all-blank) renders nothing. */
  notes?: Array<string>
}

export function ThreadJudgmentNote(props: ThreadJudgmentNoteProps) {
  const { notes } = props

  const { t } = useTranslation('app')

  const [expanded, setExpanded] = useState(false)

  const cleanNotes = (notes ?? []).filter((note) => note.trim().length > 0)

  if (cleanNotes.length === 0) {
    return null
  }

  return (
    <div className="max-w-prose pt-1" data-testid="thread-judgment-note">
      {/* Affordance matches ThreadUnplacedSources — see the note there. */}
      <button
        type="button"
        className="group -mx-2 flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-left transition-colors hover:bg-muted/60"
        data-testid="thread-judgment-note-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <Text
          as="span"
          size="xs"
          color="muted"
          className="transition-colors group-hover:text-foreground"
        >
          {t('threads.detail.judgmentNotes', { count: cleanNotes.length })}
        </Text>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground',
            expanded ? 'rotate-180' : '',
          )}
          aria-hidden
        />
      </button>
      {expanded ? (
        <ul className="mt-2 space-y-1.5" data-testid="thread-judgment-note-list">
          {cleanNotes.map((note, i) => (
            <li key={i} data-testid={`thread-judgment-note-item-${i + 1}`}>
              <Text as="span" size="xs" color="muted" className="leading-relaxed">
                {note}
              </Text>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
